import axios from 'axios';

// Smart API URL detection
const getApiBaseUrl = () => {
  // If REACT_APP_API_URL is set, use it
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // In production, try to use the same origin
  if (process.env.NODE_ENV === 'production' || window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  
  // Development fallback
  return 'http://localhost:3001';
};

const API_BASE_URL = getApiBaseUrl();

// Debug logging for API URL (only in development)
if (process.env.NODE_ENV === 'development') {
  console.log('🔗 API Base URL:', API_BASE_URL);
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle token expiration and network errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle network errors
    if (!error.response) {
      console.error('🌐 Network Error - API might be unreachable:', {
        message: error.message,
        baseURL: API_BASE_URL,
        url: error.config?.url,
        method: error.config?.method
      });
    }
    
    // Handle authentication errors
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

export interface WaiverData {
  name: string;
  email: string;
  phone?: string;
  numKids: number;
  kidsAges: number[];
  signedFor: 'self' | 'family' | 'group';
  signature: string;
}

export interface WaiverResponse {
  id: string;
  user: {
    name: string;
    email: string;
    phone?: string;
  };
  numKids: number;
  kidsAges: number[];
  signedFor: string;
  signedAt: string;
  expiresAt: string;
  isValid: boolean;
  signatureUrl: string;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface AdminStats {
  total_waivers: number;
  valid_waivers: number;
  expired_waivers: number;
  recent_waivers: number;
  this_week: number;
}

export const apiService = {
  // Waiver endpoints
  createWaiver: (data: WaiverData) => api.post('/api/waivers', data),
  checkWaiver: (email: string) => api.get(`/api/waivers/check/${email}`),
  checkWaiverByPhone: (phone: string) => api.get(`/api/waivers/check-phone/${phone}`),
  recordWaiverAccess: (waiverId: string) => api.post(`/api/waivers/${waiverId}/access`),

  // Admin endpoints
  login: (data: LoginData) => api.post('/api/admin/login', data),
  getWaivers: (params?: any) => api.get('/api/admin/waivers', { params }),
  getWaiverDetails: (id: string) => api.get(`/api/admin/waivers/${id}`),
  getStats: () => api.get('/api/admin/stats'),
};

export default api;